import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Grid,
  Autocomplete,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PageLayout from "../../components/layout/PageLayout";
import {
  useLazyGetPaymentTypesQuery,
  useLazyGetPaymentGatewaysQuery,
  useGetPaymentAccountByIdQuery,
  useCreatePaymentAccountMutation,
  useUpdatePaymentAccountMutation,
} from "../../store/api";
import { useLazySelectOptions } from "../../hooks/useLazySelectOptions";
import { useServerValidationErrors } from "../../hooks/useServerValidationErrors";
import { extractList } from "../../utils/response";
import { toast } from "react-toastify";
import { PAYMENT_ACCOUNTS_LIST_PATH } from "../../navigations/path";

const AddPaymentAccounts = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const isEdit = Boolean(id);

  const [selectedPaymentType, setSelectedPaymentType] = useState(null);
  const [selectedPaymentGateway, setSelectedPaymentGateway] = useState(null);

  const [name, setName] = useState("");
  const [descriptor, setDescriptor] = useState("");
  const [dailyLimit, setDailyLimit] = useState("");
  const [monthlyLimit, setMonthlyLimit] = useState("");
  const [status, setStatus] = useState("active");
  const { errors, applyApiError, clearError, fieldProps } =
    useServerValidationErrors();

  const [fetchPaymentTypes] = useLazyGetPaymentTypesQuery();
  const [fetchPaymentGateways] = useLazyGetPaymentGatewaysQuery();
  const { data: accountData } = useGetPaymentAccountByIdQuery(id, {
    skip: !isEdit || !id,
  });

  const paymentTypeSelect = useLazySelectOptions(fetchPaymentTypes, {
    args: { paginate: 0 },
    mapResult: (res) => {
      const list = res?.data ?? extractList(res);
      return (Array.isArray(list) ? list : []).map((item) => ({
        id: item.id,
        label: item.name,
      }));
    },
  });

  const paymentGatewaySelect = useLazySelectOptions(fetchPaymentGateways, {
    args: { paginate: 0 },
    mapResult: (res) => {
      const list = res?.data ?? extractList(res);
      return (Array.isArray(list) ? list : []).map((item) => ({
        id: item.id,
        label: item.name,
      }));
    },
  });

  const [createPaymentAccount, { isLoading: creating }] =
    useCreatePaymentAccountMutation();
  const [updatePaymentAccount, { isLoading: updating }] =
    useUpdatePaymentAccountMutation();

  const submitting = creating || updating;

  useEffect(() => {
    if (!isEdit || !accountData) return;
    const record = accountData?.data;
    if (!record) return;

    setName(record?.name || "");
    setDescriptor(record?.descriptor || "");
    setDailyLimit(record?.daily_limit || "");
    setMonthlyLimit(record?.monthly_limit || "");
    setStatus(record?.status || "active");

    setSelectedPaymentType({
      id: record?.payment_type?.id,
      label: record?.payment_type?.name,
    });

    setSelectedPaymentGateway({
      id: record?.payment_gateway?.id,
      label: record?.payment_gateway?.name,
    });
  }, [isEdit, accountData]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      payment_type_id: selectedPaymentType?.id,
      payment_gateway_id: selectedPaymentGateway?.id,
      name,
      descriptor,
      daily_limit: Number(dailyLimit),
      monthly_limit: Number(monthlyLimit),
      status,
    };

    try {
      let data;
      if (isEdit) {
        data = await updatePaymentAccount({ id, ...payload }).unwrap();
      } else {
        data = await createPaymentAccount(payload).unwrap();
      }

      toast.success(
        data?.message ||
          (isEdit
            ? "Payment Account updated successfully"
            : "Payment Account created successfully"),
      );

      navigate(PAYMENT_ACCOUNTS_LIST_PATH);
    } catch (error) {
      applyApiError(
        error,
        isEdit
          ? "Failed to update payment account"
          : "Failed to create payment account",
      );
    }
  };

  return (
    <PageLayout>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/payment-accounts")}
          >
            Back
          </Button>
        </Box>

        <Typography
          variant="h4"
          sx={{
            mb: 3,
            fontWeight: 600,
            color: "primary.main",
          }}
        >
          {isEdit ? "Edit Payment Account" : "Add Payment Account"}
        </Typography>

        <Card>
          <CardContent component="form" onSubmit={handleSubmit} sx={{ p: 3 }}>
            <Grid container spacing={2}>
              {/* Payment Type */}
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={paymentTypeSelect.options}
                  value={selectedPaymentType}
                  onChange={(_, value) => {
                    setSelectedPaymentType(value);
                    clearError("payment_type_id");
                  }}
                  onOpen={() => paymentTypeSelect.load()}
                  loading={paymentTypeSelect.loading}
                  getOptionLabel={(option) => option?.label || ""}
                  isOptionEqualToValue={(option, value) =>
                    option?.id === value?.id
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Payment Type"
                      required
                      {...fieldProps("payment_type_id")}
                    />
                  )}
                />
              </Grid>

              {/* Payment Gateway */}
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={paymentGatewaySelect.options}
                  value={selectedPaymentGateway}
                  onChange={(_, value) => {
                    setSelectedPaymentGateway(value);
                    clearError("payment_gateway_id");
                  }}
                  onOpen={() => paymentGatewaySelect.load()}
                  loading={paymentGatewaySelect.loading}
                  getOptionLabel={(option) => option?.label || ""}
                  isOptionEqualToValue={(option, value) =>
                    option?.id === value?.id
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Payment Gateway"
                      required
                      {...fieldProps("payment_gateway_id")}
                    />
                  )}
                />
              </Grid>

              {/* Account Name */}
              <Grid item xs={12}>
                <TextField
                  label="Account Name"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    clearError("name");
                  }}
                  fullWidth
                  required
                  {...fieldProps("name")}
                />
              </Grid>

              {/* Descriptor */}
              <Grid item xs={12}>
                <TextField
                  label="Descriptor"
                  value={descriptor}
                  onChange={(e) => {
                    setDescriptor(e.target.value);
                    clearError("descriptor");
                  }}
                  fullWidth
                  {...fieldProps("descriptor")}
                />
              </Grid>

              {/* Daily Limit */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Daily Limit"
                  type="number"
                  value={dailyLimit}
                  onChange={(e) => {
                    setDailyLimit(e.target.value);
                    clearError("daily_limit");
                  }}
                  fullWidth
                  required
                  {...fieldProps("daily_limit")}
                />
              </Grid>

              {/* Monthly Limit */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Monthly Limit"
                  type="number"
                  value={monthlyLimit}
                  onChange={(e) => {
                    setMonthlyLimit(e.target.value);
                    clearError("monthly_limit");
                  }}
                  fullWidth
                  required
                  {...fieldProps("monthly_limit")}
                />
              </Grid>

              {/* Status */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth error={!!errors.status}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={status}
                    label="Status"
                    onChange={(e) => {
                      setStatus(e.target.value);
                      clearError("status");
                    }}
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                  {errors.status && (
                    <FormHelperText>{errors.status}</FormHelperText>
                  )}
                </FormControl>
              </Grid>

              {/* Buttons */}
              <Grid
                item
                xs={12}
                sx={{
                  display: "flex",
                  gap: 2,
                  mt: 1,
                }}
              >
                <Button type="submit" variant="contained" disabled={submitting}>
                  {submitting ? (
                    <CircularProgress size={22} />
                  ) : isEdit ? (
                    "Edit Payment Account"
                  ) : (
                    "Create Payment Account"
                  )}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Container>
    </PageLayout>
  );
};

export default AddPaymentAccounts;
